/** @import { ProductModelElement } from "#app_lib/app_element_classes.ts" */

/**
 * @typedef {Record<string, unknown> | Document | XMLHttpRequestBodyInit | null} SLibFetchReqData
 *
 * @typedef {{
 *   url: string;
 *   method?: "GET" | "POST" | "HEAD" | "DELETE" | "PATCH" | "PUT";
 *   headers?: { [key: string]: string };
 *   mode?: "cors" | "no-cors" | "same-origin";
 *   type?: "json" | "text";
 *   dataType?: "json" | "text";
 *   body?: BodyInit | null;
 *   data?: SLibFetchReqData;
 *   success?(data: unknown, response: Response): void;
 *   error?(data: unknown, response?: Response): void;
 *   always?(): void;
 * }} SLibFetchParams
 */

class SlibMedia {
  /** @type {boolean} */
  isLarge;
  /** @type {boolean} */
  isMediumUp;
  /** @type {boolean} */
  isMedium;
  /** @type {boolean} */
  isMediumDown;
  /** @type {boolean} */
  isSmall;
  /** @type {boolean} */
  isLandscape;
  /** @type {MediaQueryList} */
  mqS;
  /** @type {MediaQueryList} */
  mqM;
  /** @type {MediaQueryList} */
  mqO;
  /** @type {NodeJS.Timeout | null} */
  tId;

  constructor() {
    this.tId = null;
    this.mqS = globalThis.matchMedia(`(max-width: ${globalThis.breakpoints.small})`);
    this.mqM = globalThis.matchMedia(`(max-width: ${globalThis.breakpoints.medium})`);
    this.mqO = globalThis.matchMedia("(orientation: landscape)");
    this.isLarge = !this.mqM.matches;
    this.isMediumUp = !this.mqS.matches;
    this.isMedium = this.mqM.matches && !this.mqS.matches;
    this.isMediumDown = this.mqM.matches;
    this.isSmall = this.mqS.matches;
    this.isLandscape = this.mqO.matches;
    this.mqS.addListener(this.handler.bind(this));
    this.mqM.addListener(this.handler.bind(this));
    this.mqO.addListener(this.handler.bind(this));
  }

  updateMedia() {
    this.isLarge = !this.mqM.matches;
    this.isMediumUp = !this.mqS.matches;
    this.isMedium = this.mqM.matches && !this.mqS.matches;
    this.isMediumDown = this.mqM.matches;
    this.isSmall = this.mqS.matches;
    this.isLandscape = this.mqO.matches;
  }

  handler() {
    if (this.tId !== null) {
      globalThis.clearTimeout(this.tId);
    }
    this.tId = globalThis.setTimeout(() => {
      this.updateMedia();
      window.dispatchEvent(new Event("mediaChange"));
    }, 1);
  }
}

class SLib {
  /**
   * @type {{
   *   beforeFocus: HTMLElement | null;
   *   container: HTMLElement;
   *   handlerFocus(evt: FocusEvent): void;
   *   handlerKeyDown(evt: KeyboardEvent): void;
   * }[]}
   */
  trapFocusStack = [];

  /** @type {{variable: string; context: string; value: string}[]} */
  cssVariables = [];

  /** @type {SlibMedia} */
  media;

  CART_EVENT_NAMES = {
    ADD_ITEM_FROM_LEGACY_VARIANT: "cart:addItemFromLegacyVariant",
    SWAP_ITEM_FROM_LEGACY_VARIANT: "cart:swapItemFromLegacyVariant",
    OPEN: "cart:open",
    CLOSE: "cart:close",
    RELOAD: "cart:reload",
    UPDATE_GLOBAL: "cart:updateGlobal",
  };

  constructor() {
    this.media = new SlibMedia();
    document.addEventListener("DOMContentLoaded", () => {
      this.domLoaded();
    });
  }

  domLoaded() {
    this.setIsMobileCssClass();
    this.userIsTabbing();
    this.loadSectionsAsync();
    this.setHeaderSubmenus();
    this.setHeaderSearch();
    this.setScrollToFragment();
    this.setAnchorClickHandlers();
    this.customProductQuickView();
  }

  throttle(/** @type {(...args: unknown[]) => void} */ callback, /** @type {number} */ limit) {
    let waiting = false;
    /** @type {(() => void) | null} */
    let callAfterWaiting = null;
    return function main(/** @type {unknown[]} */ ...args1) {
      if (!waiting) {
        // @ts-expect-error
        // biome-ignore lint/complexity/noUselessThisAlias: Disable
        const this2 = this;
        callback.apply(this2, args1);
        waiting = true;
        setTimeout(() => {
          waiting = false;
          if (callAfterWaiting) {
            callAfterWaiting();
            callAfterWaiting = null;
          }
        }, limit);
      } else {
        callAfterWaiting = function (/** @type {unknown[]} */ ...args2) {
          // @ts-expect-error
          // biome-ignore lint/complexity/noUselessThisAlias: Disable
          const this2 = this;
          callback.apply(this2, args2);
        };
      }
    };
  }

  getFocusableElements(/** @type {HTMLElement} */ container) {
    /** @type {NodeListOf<HTMLElement>} */
    const items = container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, " +
        "input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    );
    return [...items];
  }

  trapFocus(/** @type {HTMLElement} */ container, /** @type {HTMLElement=} */ elementToFocus = undefined) {
    const trapItem = {
      beforeFocus: /** @type {HTMLElement | null} */ (document.activeElement),
      container: container,
      handlerFocus: (/** @type {FocusEvent} */ evt) => {
        if (this.trapFocusStack.length === trapIndex) {
          const eventTarget = /** @type {HTMLElement | null} */ (evt.target);
          if (container !== evt.target && !container.contains(eventTarget)) {
            container.focus();
          }
        }
      },
      handlerKeyDown: (/** @type {KeyboardEvent} */ evt) => {
        if (evt.code.toUpperCase() !== "TAB") {
          return;
        }
        const elements = this.getFocusableElements(container);
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (evt.target === last && !evt.shiftKey) {
          evt.preventDefault();
          first.focus();
        }
        if ((evt.target === container || evt.target === first) && evt.shiftKey) {
          evt.preventDefault();
          last.focus();
        }
      },
    };
    this.trapFocusStack.push(trapItem);
    const trapIndex = this.trapFocusStack.length;
    const elementToFocus2 = elementToFocus ?? container;
    container.setAttribute("tabindex", "-1");
    elementToFocus2.focus();
    container.addEventListener("keydown", trapItem.handlerKeyDown);
    document.addEventListener("focusin", trapItem.handlerFocus);
  }

  removeTrapFocus() {
    const lastItem = this.trapFocusStack.pop();
    if (lastItem) {
      if (lastItem.beforeFocus) {
        lastItem.beforeFocus.focus();
      }
      if (lastItem.container) {
        lastItem.container.removeAttribute("tabindex");
        lastItem.container.removeEventListener("keydown", lastItem.handlerKeyDown);
      }
      document.removeEventListener("focusin", lastItem.handlerFocus);
    }
  }

  openModal(
    /** @type {HTMLElement | string} */ content,
    /** @type {{css?: string; onClose?: () => void}} */ options = { css: undefined, onClose: undefined },
  ) {
    const this2 = this;
    const { css, onClose } = options;
    /** @type {tingle.TingleOptions} */
    const defaults = {
      closeMethods: ["overlay", "button", "escape"],
      cssClass: css === undefined ? [] : [css],
      onOpen: function () {
        this2.trapFocus(this.modal);
        return true;
      },
      onClose: () => {
        if (onClose !== undefined) {
          onClose();
        }
        this.removeTrapFocus();
        return true;
      },
    };
    globalThis.modal = new tingle.modal({ ...defaults, ...options });
    globalThis.modal.setContent(content);
    globalThis.modal.open();
  }

  closeModal() {
    if (globalThis.modal !== null && globalThis.modal !== undefined) {
      globalThis.modal.close();
    }
  }

  handleize(/** @type {string} */ str) {
    return str
      .toLowerCase()
      .replace(/\'|\"/g, "")
      .replace(/[^\w\u00C0-\u024f]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  getQueryParam(/** @type {string} */ name, /** @type {string=} */ url = undefined) {
    const url2 = url ?? globalThis.location.href;
    const name2 = name.replace(/[\[\]]/g, "\\$&");
    const regex = new RegExp("[?&]" + name2 + "(=([^&#]*)|&|#|$)");
    const results = regex.exec(url2);
    if (!results) {
      return null;
    }
    if (!results[2]) {
      return "";
    }
    return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

  randomstring(/** @type {number} */ len) {
    const randomchar = () => {
      const n = Math.floor(Math.random() * 62);
      if (n < 10) {
        //1-10
        return n;
      }
      if (n < 36) {
        //A-Z
        return String.fromCharCode(n + 55);
      }
      //a-z
      return String.fromCharCode(n + 61);
    };
    let s = "";
    while (s.length < len) {
      s += randomchar();
    }
    return s;
  }

  validateEmail(/** @type {string} */ email) {
    const re = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
    return re.test(email);
  }

  formatMoney(/** @type {number | string} */ cents, /** @type {string=} */ format = undefined) {
    const cents2 = (() => {
      if (typeof cents === "number") {
        return Number.isNaN(cents) ? 0 : cents;
      }
      if (typeof cents === "string") {
        const cents3 = Number(cents.replace(".", ""));
        return Number.isNaN(cents3) ? 0 : cents3;
      }
      return 0;
    })();
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format ?? globalThis.money_format;
    const formatWithDelimiters = (
      /** @type {number} */ num,
      /** @type {number=} */ precision = undefined,
      /** @type {string=} */ thousands = undefined,
      /** @type {string=} */ decimal = undefined,
    ) => {
      const precision2 = precision ?? 2;
      const thousands2 = thousands ?? ",";
      const decimal2 = decimal ?? ".";
      const num2 = (num / 100.0).toFixed(precision2);
      const parts = num2.split(".");
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands2);
      const cents4 = parts[1] ? decimal2 + parts[1] : "";
      return dollars + cents4;
    };
    const formatMatch = formatString.match(placeholderRegex);
    const value = (() => {
      if (formatMatch === null) {
        console.error("SLib.formatMoney: This might be an error (1)");
        return formatWithDelimiters(cents2, 0);
      }
      const formatVal = formatMatch[1];
      if (formatVal === "amount") {
        return formatWithDelimiters(cents2, 2);
      }
      if (formatVal === "amount_no_decimals") {
        return formatWithDelimiters(cents2, 0);
      }
      if (formatVal === "amount_with_comma_separator") {
        return formatWithDelimiters(cents2, 2, ".", ",");
      }
      if (formatVal === "amount_no_decimals_with_comma_separator") {
        return formatWithDelimiters(cents2, 0, ".", ",");
      }
      console.error("SLib.formatMoney: This might be an error (2)");
      return formatWithDelimiters(cents2, 0);
    })();
    return formatString.replace(placeholderRegex, value);
  }

  onImagesLoaded(/** @type {HTMLImageElement[]} */ images, /** @type {() => void} */ callback) {
    let imagesLoaded = 0;
    const loadFunction = () => {
      imagesLoaded++;
      if (imagesLoaded === images.length) {
        callback();
      }
    };
    if (images.length > 0) {
      for (const obj of images) {
        const srcVal = obj.getAttribute("src");
        if (srcVal === null) {
          continue;
        }
        const img = new Image();
        img.onload = loadFunction;
        img.onerror = loadFunction;
        img.src = srcVal;
      }
      return;
    }
    callback();
  }

  pauseAllMedia(/** @type {HTMLElement=} */ container = undefined) {
    const container2 = container ?? globalThis.document;
    /** @type {NodeListOf<HTMLIFrameElement | HTMLVideoElement | ProductModelElement>} */
    const elms = container2.querySelectorAll(".js-youtube, .js-vimeo, video, product-model");
    for (const media of elms) {
      this.pauseMedia(media);
    }
  }

  pauseMedia(/** @type {(HTMLIFrameElement | HTMLVideoElement | ProductModelElement | null)=} */ media = undefined) {
    if (media === null || media === undefined) {
      return;
    }
    if (media.classList.contains(".js-youtube")) {
      const media2 = /** @type {HTMLIFrameElement} */ (media);
      if (media2.contentWindow !== null) {
        media2.contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: "" }), "*");
      }
      return;
    }
    if (media.classList.contains(".js-vimeo")) {
      const media2 = /** @type {HTMLIFrameElement} */ (media);
      if (media2.contentWindow !== null) {
        media2.contentWindow.postMessage(JSON.stringify({ method: "pause" }), "*");
      }
      return;
    }
    if (media.tagName.toLowerCase() === "video") {
      const media2 = /** @type {HTMLVideoElement} */ (media);
      media2.pause();
      return;
    }
    if (media.tagName.toLowerCase() === "product-model") {
      //
      // TODO: Does this still throw an error on Ipad 6th Gen?
      //
      // const media2 = media as ProductModelElement;
      // if (media2.modelViewerUI !== null && media2.modelViewerUI !== undefined) {
      //  media2.modelViewerUI.pause()
      // }
      return;
    }
  }

  playMedia(/** @type {(HTMLIFrameElement | HTMLVideoElement | ProductModelElement | null)=} */ media = undefined) {
    if (media === null || media === undefined) {
      return;
    }
    if (media.classList.contains(".js-youtube")) {
      const media2 = /** @type {HTMLIFrameElement} */ (media);
      if (media2.contentWindow !== null) {
        media2.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: "" }), "*");
      }
      return;
    }
    if (media.classList.contains(".js-vimeo")) {
      const media2 = /** @type {HTMLIFrameElement} */ (media);
      if (media2.contentWindow !== null) {
        media2.contentWindow.postMessage(JSON.stringify({ method: "play" }), "*");
      }
      return;
    }
    if (media.tagName.toLowerCase() === "video") {
      const media2 = /** @type {HTMLVideoElement} */ (media);
      media2.play();
      return;
    }
    if (media.tagName.toLowerCase() === "product-model") {
      //
      // TODO: Does this still throw an error on Ipad 6th Gen?
      //
      // const media2 = media as ProductModelElement;
      // if (media2.modelViewerUI !== null && media2.modelViewerUI !== undefined) {
      //  media2.modelViewerUI.play()
      // }
      return;
    }
  }

  isVisible(/** @type {HTMLElement} */ element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length > 0);
  }

  isInViewport(/** @type {HTMLElement} */ element) {
    const { top, bottom } = element.getBoundingClientRect();
    const vHeight = window.innerHeight || document.documentElement.clientHeight;
    return (top > 0 || bottom > 0) && top < vHeight;
  }

  slideTransitionEnd(/** @type {TransitionEvent} */ evt) {
    if (evt.propertyName !== "max-height") {
      return;
    }
    if (evt.target === null) {
      return;
    }
    const panel = /** @type {HTMLElement} */ (evt.target);
    if (panel.style.maxHeight === "0px") {
      panel.style.visibility = "hidden";
      panel.style.overflow = "hidden";
    } else {
      panel.style.maxHeight = "none";
      panel.style.overflow = "visible";
    }
  }

  expand(
    /** @type {HTMLElement[]} */ panels,
    /** @type {Partial<{duration: Number; effect: string}>=} */ options = undefined,
  ) {
    const options1 = options === undefined ? {} : options;
    const settingsOrig = { duration: 400, effect: "ease-out" };
    const settings = { ...settingsOrig, ...options1 };
    for (const panel of panels) {
      panel.style.visibility = "visible";
      panel.removeEventListener("transitionend", this.slideTransitionEnd);
      panel.style.overflow = "hidden";
      panel.style.transition = "max-height " + settings.duration + "ms " + settings.effect;
      const height = panel.scrollHeight + "px";
      panel.style.maxHeight = height;
      panel.addEventListener("transitionend", this.slideTransitionEnd);
    }
  }

  collapse(
    /** @type {HTMLElement[]} */ panels,
    /** @type {Partial<{duration: Number; effect: string}>=} */ options = undefined,
  ) {
    const options1 = options === undefined ? {} : options;
    const settingsOrig = { duration: 400, effect: "ease-out" };
    const settings = { ...settingsOrig, ...options1 };
    for (const panel of panels) {
      panel.removeEventListener("transitionend", this.slideTransitionEnd);
      panel.style.transition = "max-height " + settings.duration + "ms " + settings.effect;
      const height = panel.scrollHeight + "px";
      panel.style.maxHeight = height;
      // Workaround to instant collapse
      setTimeout(() => {
        panel.style.overflow = "hidden";
        panel.style.maxHeight = "0px";
        panel.addEventListener("transitionend", this.slideTransitionEnd);
      }, 100);
    }
  }

  toggle(
    /** @type {HTMLElement[]} */ panels,
    /** @type {Partial<{duration: Number; effect: string}>=} */ options = undefined,
  ) {
    for (const panel of panels) {
      const style = window.getComputedStyle(panel);
      if (style.display === "none" || style.maxHeight === "0px") {
        this.expand([panel], options);
      } else {
        this.collapse([panel], options);
      }
    }
  }

  scrollToElement(
    /** @type {string} */ selector,
    /** @type {number=} */ offsetTop = undefined,
    /** @type {ScrollBehavior=} */ behavior = undefined,
  ) {
    const compHeaderOffset = getComputedStyle(document.body).getPropertyValue("--header-offset");
    const headerOffset = (() => {
      const parsedOffset = Number(compHeaderOffset.replace("px", ""));
      if (!Number.isNaN(parsedOffset) && parsedOffset !== 0) {
        return parsedOffset;
      }
      // Fallback: try to get header height directly
      const headerElement = document.getElementById("header") || document.querySelector("#shopify-section-header");
      return headerElement ? headerElement.offsetHeight : 0;
    })();
    /** @type {HTMLElement | null} */
    const elm = document.querySelector(selector);
    if (elm === null) {
      return;
    }
    const elmTop = elm.getBoundingClientRect().top;
    const offsetTop2 = offsetTop ?? 20;
    const newTop = elmTop - headerOffset - offsetTop2;
    globalThis.scrollBy({
      left: 0,
      top: newTop,
      behavior: behavior ?? "smooth",
    });
  }

  matchHeight(/** @type {HTMLElement[]} */ elements) {
    for (const elm of elements) {
      elm.style.minHeight = "";
    }
    let height = 0;
    for (const elm of elements) {
      height = Math.max(height, elm.offsetHeight);
    }
    for (const elm of elements) {
      elm.style.minHeight = height + "px";
    }
  }

  hideScroll() {
    // globalThis.document.body.style.overflow = 'hidden';
    globalThis.document.documentElement.style.overflow = "hidden";
  }

  showScroll() {
    // globalThis.document.body.style.overflow = '';
    globalThis.document.documentElement.style.overflow = "";
  }

  showOverlay() {
    // NOTHING
  }

  hideOverlay() {
    // NOTHING
  }

  showLoadingOverlay() {
    let overlay = document.getElementById("loading-overlay");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "loading-overlay";
      overlay.innerHTML = '<div class="loading-spinner"></div>';
      document.body.appendChild(overlay);
    }
    document.body.classList.add("show-loading-overlay");
  }

  hideLoadingOverlay() {
    document.body.classList.remove("show-loading-overlay");
  }

  addToCartSuccess() {
    document.dispatchEvent(new Event("cart:reload"));
    document.dispatchEvent(new Event("cart:updateGlobal"));
    document.dispatchEvent(new Event("cart:open"));
  }

  addToCartFail(/** @type {string} */ _msg) {
    // NOTHING
  }

  dispatchEvent(/** @type {HTMLElement} */ target, /** @type {string} */ eventName) {
    const event = document.createEvent("HTMLEvents");
    event.initEvent(eventName, false, true);
    target.dispatchEvent(event);
  }

  checkout() {
    const getCookie = (/** @type {string} */ name) => {
      const value = `; ${globalThis.document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        return parts[1].split(";").shift();
      }
      return null;
    };
    const deleteCookie = (/** @type {string} */ name) => {
      globalThis.document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    };
    this.getJson({
      url: "/cart.json",
      success: (data) => {
        const data2 = /** @type {{ items: {properties?: {_discount_code: string}}[] }} */ (data);
        let discountCode;
        const cookieDiscount = getCookie("affiliateDiscount");
        for (const item of data2.items) {
          if (item.properties && item.properties._discount_code) {
            discountCode = item.properties._discount_code;
          }
        }
        if (cookieDiscount) {
          discountCode = cookieDiscount;
          deleteCookie("affiliateDiscount");
        }
        globalThis.location.href = "/checkout" + (discountCode ? "?discount=" + discountCode : "");
      },
    });
  }

  /**
   * TODO: using `eval()` can be risky. We may want to see how we can remove this entirely.
   */
  globalEval(/** @type {HTMLElement | string} */ textHtmlOrElement) {
    const makeDoc = () => {
      if (typeof textHtmlOrElement === "string") {
        return new DOMParser().parseFromString(textHtmlOrElement, "text/html");
      }
      return textHtmlOrElement;
    };
    const doc = makeDoc();
    const scripts = doc.querySelectorAll("script");
    for (const script of scripts) {
      if (script.src) {
        const d = document.createElement("script");
        for (const attr of script.attributes) {
          d.setAttribute(attr.name, attr.value);
          const appended = globalThis.document.head.appendChild(d);
          const appendedParent = /** @type {HTMLElement | null} */ (appended.parentNode);
          if (appendedParent === null) {
            continue;
          }
          appendedParent.removeChild(d);
        }
      } else {
        if (script.type !== "application/json") {
          // biome-ignore lint/security/noGlobalEval: We should get rid of this eval call eventually
          eval(script.innerHTML);
        }
      }
    }
  }

  ajax(/** @type {SLibFetchParams=}>} */ options = undefined) {
    const options1 = {
      type: "get",
      data: null,
    };
    const options2 = options === undefined ? {} : options;
    const options3 = { ...options1, ...options2 };
    const options4 = /** @type {{type: string; url: string; data: Document | XMLHttpRequestBodyInit | null}} */ (
      options3
    );
    const req = new XMLHttpRequest();
    /** @type {Promise<XMLHttpRequest> & Partial<{ajaxCall: XMLHttpRequest}>} */
    const promise = new Promise((resolve, reject) => {
      req.open(options4.type, options4.url, true);
      req.onreadystatechange = () => {
        if (req.readyState !== 4) {
          return;
        }
        if (req.status === 200) {
          resolve(req);
        } else {
          reject(req);
        }
      };
      req.send(options4.data);
    });
    // TODO: What is this for?
    promise.ajaxCall = req;
    return promise;
  }

  get(/** @type {SLibFetchParams} */ params) {
    return this.fetch({ ...params, type: "text" });
  }

  getJson(/** @type {SLibFetchParams} */ params) {
    return this.fetch({ ...params, type: "json" });
  }

  post(/** @type {SLibFetchParams} */ params) {
    params.type = params.type || "json";
    if (params.data) {
      if (typeof params.data === "string") {
        params.headers = params.headers || { "Content-Type": "application/x-www-form-urlencoded" };
        params.body = params.data;
      } else {
        if (!(params.data instanceof FormData)) {
          params.headers = params.headers || { "Content-Type": "application/json" };
          params.body = JSON.stringify(params.data);
        } else {
          params.body = params.data;
        }
      }
    }
    return this.fetch({ ...params, method: "POST" });
  }

  fetch(/** @type {SLibFetchParams} */ params) {
    const controller = new AbortController();
    const config = {
      method: params.method || "GET",
      headers: params.headers,
      mode: params.mode || "cors",
      body: params.body,
      signal: controller.signal,
    };
    const parseResponse = (/** @type {string} */ text) => {
      if (params.type === "json") {
        try {
          const data = /** @type {unknown} */ (JSON.parse(text));
          return data;
        } catch {
          console.error("parseResponse error, cannot JSON.parse: ", text);
        }
      }
      return text;
    };
    fetch(params.url, config)
      .then((response) => {
        response.text().then((text) => {
          if (response.ok) {
            if (params.success) {
              params.success(parseResponse(text), response);
            }
          } else {
            if (params.error) {
              params.error(parseResponse(text), response);
            }
          }
          if (params.always) {
            params.always();
          }
        });
      })
      .catch((e) => {
        if (e.name !== "AbortError") {
          if (params.error) {
            params.error(parseResponse(""));
          }
        }
        if (params.always) {
          params.always();
        }
      });
    return controller;
  }

  getDataFromUrl(/** @type {string} */ url) {
    /** @type {Promise<string>} */
    const promise = new Promise((resolve) => {
      this.get({
        url: url,
        success: (data) => {
          const data2 = /** @type {string} */ (data);
          resolve(data2);
        },
      });
    });
    return promise;
  }

  setHeaderSubmenus() {
    /** @type {NodeListOf<HTMLElement>} */
    const hasDropDowns = document.querySelectorAll(".header-main-menu .has-dropdown:not(.submenu-initialized)");
    for (const obj of hasDropDowns) {
      obj.classList.add("submenu-initialized");
      globalThis
        .hoverintent(
          obj,
          () => {
            /** @type {HTMLElement | null} */
            const subMenu = obj.querySelector(".submenu");
            if (subMenu === null) {
              return;
            }
            this.expand([subMenu]);
          },
          () => {
            /** @type {HTMLElement | null} */
            const subMenu = obj.querySelector(".submenu");
            if (subMenu === null) {
              return;
            }
            this.collapse([subMenu]);
          },
        )
        .options({ timeout: 300 });
      for (const obj2 of obj.querySelectorAll("a")) {
        obj2.addEventListener("keydown", (ev) => {
          const nextSibling = /** @type {HTMLElement | null} */ (obj2.nextSibling);
          if (nextSibling === null) {
            return;
          }
          if (ev.which === 32) {
            //Spacebar
            ev.preventDefault();
            this.toggle([nextSibling]);
          }
          if (ev.which === 40) {
            //Arrow Down
            ev.preventDefault();
            this.expand([nextSibling]);
          }
          if (ev.which === 38) {
            //Arrow Up
            ev.preventDefault();
            this.collapse([nextSibling]);
          }
          if (obj2.closest(".submenu")) {
            if (ev.which === 39) {
              //Arrow Right
              ev.preventDefault();
              this.expand([nextSibling]);
            }
            if (ev.which === 37) {
              //Arrow Left
              ev.preventDefault();
              this.collapse([nextSibling]);
            }
          }
        });
        //
        // Disabling this code for now since it is causing errors which pre-date this PR.
        // `obj2.nextSibling` points to a text node instead of the next submenu.
        // See Issue 372
        //
        // obj2.addEventListener("focus", (ev) => {
        //  const nextSibling = (obj2.nextSibling); // @type {HTMLElement | null}
        //  if (nextSibling === null) {
        //    return;
        //  }
        //  ev.preventDefault();
        //  this.expand([nextSibling]);
        // });
      }
    }
  }

  setHeaderSearch() {
    const bar = document.getElementById("search-bar");
    if (bar === null) {
      return;
    }
    const header = document.querySelector(".header-inner");
    if (header === null) {
      throw new Error("header inner not found");
    }
    header.appendChild(bar);
    let stateOpen = false;
    const button = header.querySelector(".trigger-search");
    if (button === null) {
      throw new Error("trigger search button not found");
    }
    const buttonClose = header.querySelector("#header .close-search-bar");
    const toggleSearchBar = () => {
      stateOpen = !stateOpen;
      if (stateOpen) {
        button.setAttribute("aria-expanded", "true");
        bar.classList.remove("close");
        bar.classList.add("open");
        globalThis.setTimeout(() => {
          /** @type {HTMLInputElement | null} */
          const qElm = bar.querySelector('[name="q"]');
          if (qElm !== null) {
            qElm.focus();
          }
        }, 100);
        return;
      }
      button.setAttribute("aria-expanded", "false");
      const searchForm = bar.querySelector("search-form");
      if (searchForm !== null) {
        searchForm.clearResults();
      }
      bar.classList.remove("open");
      bar.classList.add("close");
    };
    button.addEventListener("click", toggleSearchBar);
    if (buttonClose !== null) {
      buttonClose.addEventListener("click", toggleSearchBar);
    }
  }

  updateCartCount(/** @type {HTMLDivElement} */ cartCountElm) {
    if (globalThis.shopifyCartData === undefined) {
      return;
    }
    const cartCount = globalThis.shopifyCartData.totalQuantity;
    cartCountElm.textContent = cartCount > 0 ? cartCount.toString() : "";
  }

  initCartCountElms() {
    /** @type {NodeListOf<HTMLDivElement>} */
    const cartCountElms = document.querySelectorAll(".cart-count");
    for (const cartCountElm of cartCountElms) {
      this.updateCartCount(cartCountElm);
    }
  }

  initCartClickHandlers() {
    /** @type {NodeListOf<HTMLAnchorElement>} */
    const cartTriggerElms = document.querySelectorAll(".trigger-cart");
    for (const cartTrigger of cartTriggerElms) {
      cartTrigger.addEventListener("click", (event) => {
        event.preventDefault();
        document.dispatchEvent(new Event(this.CART_EVENT_NAMES.OPEN));
      });
    }
  }

  initTopUtilsCart() {
    this.initCartCountElms();
    document.addEventListener(this.CART_EVENT_NAMES.UPDATE_GLOBAL, this.initCartCountElms.bind(this));
    this.initCartClickHandlers();
  }

  loadSectionsAsync() {
    let mobileMenuLoaded = false;
    const loadMobileMenu = () => {
      if (document.querySelector(".mobile-menu-button") === null) {
        return;
      }
      if (this.media.isMediumDown) {
        const elm = document.getElementById("shopify-section-mobile_menu");
        if (!mobileMenuLoaded && elm === null) {
          mobileMenuLoaded = true;
          globalThis.removeEventListener("mediaChange", loadMobileMenu);
          this.getJson({
            url: "/?sections=mobile_menu",
            success: (data) => {
              const data2 = /** @type {{ mobile_menu?: string }} */ (data);
              if (data2["mobile_menu"]) {
                const header = document.getElementById("header");
                if (header !== null) {
                  header.insertAdjacentHTML("afterend", data2["mobile_menu"]);
                }
                this.globalEval(data2["mobile_menu"]);
              }
            },
          });
        }
      }
    };
    globalThis.addEventListener("mediaChange", loadMobileMenu);
    loadMobileMenu();
    this.initTopUtilsCart();
    const loadColorSwatches = () => {
      this.getJson({
        url: "/?sections=color_swatches",
        success: (data) => {
          const data2 = /** @type {{ color_swatches?: string }} */ (data);
          if (data2["color_swatches"]) {
            globalThis.document.body.insertAdjacentHTML("afterbegin", data2["color_swatches"]);
          }
        },
      });
    };
    loadColorSwatches();
    const megaMenus = globalThis.MEGA_MENU.megaMenus;
    const megaMenusParentLinks = globalThis.MEGA_MENU.megaMenusParentLinks;
    const hasMegaMenu = globalThis.MEGA_MENU.hasMegaMenu;
    let megaMenuLoaded = false;
    const loadMegaMenu = () => {
      if (!megaMenuLoaded && this.media.isLarge) {
        megaMenuLoaded = true;
        for (const parentKey of megaMenusParentLinks) {
          const existingSubmenu = globalThis.document.querySelector(
            '.header-main-menu [data-parent="' + parentKey + '"] .submenu',
          );
          if (existingSubmenu) {
            existingSubmenu.remove();
          }
        }
        globalThis.removeEventListener("mediaChange", loadMegaMenu);
        this.getJson({
          url: "/?sections=" + megaMenus.join(","),
          success: (data) => {
            const data2 = /** @type {{ [key: string]: string | undefined }} */ (data);
            for (const item of megaMenus) {
              if (data2[item]) {
                document.body.insertAdjacentHTML("beforeend", data2[item]);
                this.globalEval(data2[item]);
              }
            }
          },
        });
      }
    };
    if (hasMegaMenu) {
      globalThis.addEventListener("mediaChange", loadMegaMenu);
      loadMegaMenu();
    }
  }

  userIsTabbing() {
    globalThis.addEventListener("keydown", (ev) => {
      if (ev.keyCode === 9) {
        // tab
        globalThis.document.body.classList.add("user-is-tabbing");
      }
    });
    globalThis.document.addEventListener("click", () => {
      globalThis.document.body.classList.remove("user-is-tabbing");
    });
  }

  handleLocationHashScroll(/** @type {string} */ hash) {
    if (!hash.startsWith("#")) {
      return;
    }
    /** @type {HTMLElement | null} */
    const target = document.querySelector(hash);
    if (target === null) {
      return;
    }
    const top = target.offsetTop;
    // special handling for pages with floating nav since it impacts offset when "sticky"
    const hasFloatingNav = document.getElementById("floating-nav");
    if (hasFloatingNav) {
      const isMobile = document.body.clientWidth <= 900;
      const offset = isMobile ? 150 : 200;
      globalThis.scrollTo({ top: top - offset, behavior: "smooth" });
    } else {
      globalThis.scrollTo({ top: top - 100, behavior: "smooth" });
    }
  }

  setCssVariable(/** @type {string} */ variable, /** @type {string} */ value, /** @type {string} */ context = ":root") {
    const existingItem = this.cssVariables.find((x) => x.variable === variable && x.context === context);
    if (existingItem !== undefined) {
      existingItem.value = value;
    } else {
      this.cssVariables.push({ variable, value, context });
    }
    const cssVariablesElement = document.getElementById("css-variables");
    if (cssVariablesElement !== null) {
      cssVariablesElement.remove();
    }
    let variables = "";
    const vars = this.cssVariables.map((x) => x.context).filter((x, i, a) => a.indexOf(x) === i);
    for (const varContext of vars) {
      variables += varContext + " {";
      const vars2 = this.cssVariables.filter((x) => x.context === varContext);
      for (const cssVariable of vars2) {
        variables += `--${cssVariable.variable}: ${cssVariable.value};`;
      }
      variables += "}";
    }
    document.head.insertAdjacentHTML("beforeend", `<style id="css-variables">${variables}</style>`);
  }

  setClickHandlerInterImgLink(/** @type { HTMLAnchorElement} */ element) {
    element.addEventListener("click", (ev) => {
      ev.preventDefault();
      const tempScrollPositionVar = document.documentElement.scrollTop;
      globalThis.location.hash = element.href.split("#")[1];
      document.documentElement.scrollTop = tempScrollPositionVar;
      return false;
    });
  }

  setClickHandlerImages(/** @type {HTMLAnchorElement} */ element) {
    element.addEventListener("click", (ev) => {
      ev.preventDefault();
      const elm = globalThis.document.querySelector('[data-img-link="' + element.href + '"]');
      if (elm !== null) {
        elm.classList.add("flex");
      }
      return false;
    });
  }

  doesOriginMatch(/** @type {string} */ href) {
    // If empty string, it kind of matches but also kind of does not.
    // In this case we should probably decide that it does not.
    if (href.trim() === "") {
      return false;
    }
    const urlObj1 = (() => {
      try {
        return new URL(href);
      } catch {
        return null;
      }
    })();
    const urlObj2 =
      urlObj1 ??
      (() => {
        try {
          return new URL(href, globalThis.location.origin);
        } catch {
          return null;
        }
      })();
    if (urlObj2 === null) {
      return false;
    }
    return urlObj2.origin === globalThis.location.origin;
  }

  getHrefHashFromAnchorElement(/** @type {HTMLAnchorElement} */ element) {
    if (!this.doesOriginMatch(element.href)) {
      return null;
    }
    const parts = element.href.split("#", 2);
    if (parts.length < 2 || parts[1] === "") {
      return null;
    }
    const sel = `#${parts[1]}`;
    return sel;
  }

  setClickHandlerFragmentLinks(/** @type {HTMLAnchorElement} */ element) {
    // Only do in page scroll to internal links, not for external links
    const sel = this.getHrefHashFromAnchorElement(element);
    if (sel === null) {
      return;
    }
    /** @type {HTMLElement | null} */
    const target = document.querySelector(sel);
    if (target === null) {
      return;
    }
    element.addEventListener("click", (ev) => {
      ev.preventDefault();
      this.handleLocationHashScroll(sel);
      return false;
    });
  }

  setAnchorClickHandlers() {
    /** @type {NodeListOf<HTMLAnchorElement>} */
    const anchors = document.querySelectorAll(":not(.ui-root) a");
    for (const element of anchors) {
      if (element.classList.value.indexOf("interactive-image-link") !== -1) {
        this.setClickHandlerInterImgLink(element);
        continue;
      }
      if (element.href.endsWith(".png") || element.href.endsWith(".jpg")) {
        this.setClickHandlerImages(element);
        continue;
      }
      this.setClickHandlerFragmentLinks(element);
    }
  }

  setIsMobileCssClass() {
    if (!globalThis.isMobile.any) {
      document.body.classList.add("not-mobile");
    }
  }

  setScrollToFragment() {
    globalThis.setTimeout(() => {
      this.handleLocationHashScroll(globalThis.location.hash);
    }, 500);
  }

  customProductQuickView() {
    const appendToHref = "?view=quick-view&sections=main_product_quick_view_custom";
    /** @type {NodeListOf<HTMLAnchorElement>} */
    const quickViews = globalThis.document.querySelectorAll(".quick-view-custom");
    for (const element of quickViews) {
      element.addEventListener("click", (ev) => {
        ev.preventDefault();
        this.getJson({
          url: element.href + appendToHref,
          success: (data) => {
            const data2 = /** @type {{ main_product_quick_view_custom: string }} */ (data);
            this.openModal(data2["main_product_quick_view_custom"], { css: "product-quick-view" });
            this.globalEval(data2["main_product_quick_view_custom"]);
          },
        });
      });
    }
  }

  photoSwipe(/** @type {Iterable<HTMLElement>} */ psItemsOrig, /** @type {HTMLElement} */ item) {
    const psItems = [...psItemsOrig];
    /** @type {HTMLElement | null} */
    const pswpElement = document.querySelector(".pswp");
    if (pswpElement === null) {
      console.error("PhotoSwipe: pswpElement not found");
      return;
    }
    const index = psItems.findIndex((x) => x === item);
    /** @type {PhotoSwipe.PhotoSwipeItems} */
    const items = [];
    for (const psItem of psItems) {
      if (psItem.tagName.toLowerCase() === "video" || psItem.tagName.toLowerCase() === "iframe") {
        items.push({ html: '<div class="video">' + psItem.outerHTML + "</div>" });
      } else {
        items.push({ src: psItem.dataset.file ?? "", w: 0, h: 0, title: psItem.dataset.title ?? "" });
      }
    }
    const options = {
      index: index,
      history: false,
      focus: false,
      showAnimationDuration: 0,
      hideAnimationDuration: 0,
      tapToClose: true,
    };
    const ps = new PhotoSwipe(pswpElement, globalThis.PhotoSwipeUI_Default, items, options);
    ps.listen("gettingData", (_index, item) => {
      if (item.html === undefined) {
        if (item.w < 1 || item.h < 1) {
          const img = new Image();
          img.onload = () => {
            item.w = img.width;
            item.h = img.height;
            ps.updateSize(true);
          };
          img.src = item.src;
        }
      }
    });
    ps.listen("afterInit", () => {
      this.trapFocus(pswpElement);
    });
    ps.listen("destroy", () => {
      this.removeTrapFocus();
    });
    ps.init();
    return ps;
  }

  getAncestorElement(/**@type {string} */ selectorString, /**@type {HTMLElement} */ referenceElement) {
    // Returns the first ancestor element that matches the selector starting from the referenceElement child up

    if (!referenceElement) {
      throw new Error("Invalid reference element.");
    }

    const _element = referenceElement.closest(selectorString);

    if (_element === null) {
      throw new Error(`${selectorString} element not found.`);
    }

    return _element;
  }
}

export { SLib };
